import React from 'react';
import ReactDOM from 'react-dom/client';
import './I18n/config';
import '@/styles/index.css';
import App from './App';
import { initializeTenant, migrateToSessionStorage, getTenantId } from './services/tenant/tenantService.js';
import { initializeHostMapping } from './services/tenant/hostMappingService.js';
import { captureUtmParams } from './utils/utmUtils';
import Splash from './Components/layout/Splash/index.js';
import { initializeNewRelic, logAssetLoadFailure } from './observability/newrelic';

initializeNewRelic();

// Circuit breaker para o handler de erro de script/link mais abaixo: sem isso,
// um chunk quebrado (ex.: index.html em cache do SW apontando pra um asset que
// falhou) causava reload infinito, já que window.location.reload() é servido
// pelo próprio precache do Service Worker (não busca um index.html novo).
const SCRIPT_ERROR_RELOAD_KEY = 'scriptErrorReloadCount';
const MAX_AUTO_RELOADS = 2;

const getReloadCount = () => {
  try {
    return Number(sessionStorage.getItem(SCRIPT_ERROR_RELOAD_KEY) || '0');
  } catch {
    return 0;
  }
};

const setReloadCount = (value) => {
  try {
    sessionStorage.setItem(SCRIPT_ERROR_RELOAD_KEY, String(value));
  } catch {
    // ignora — pior caso, breaker não persiste entre reloads
  }
};

const clearReloadCount = () => {
  try {
    sessionStorage.removeItem(SCRIPT_ERROR_RELOAD_KEY);
  } catch {
    // ignora
  }
};

// Tela de fallback em DOM puro (sem depender de React) — precisa funcionar
// mesmo se o JS do app estiver quebrado, que é justamente o cenário aqui.
const showManualReloadFallback = () => {
  document.body.innerHTML = `
    <div style="display:flex;flex-direction:column;align-items:center;
      justify-content:center;height:100vh;font-family:sans-serif;
      text-align:center;padding:24px;">
      <h1 style="font-size:20px;margin-bottom:12px;">Não foi possível carregar o aplicativo</h1>
      <p style="color:#555;margin-bottom:20px;">Isso pode acontecer por uma conexão instável. Tente novamente.</p>
      <button id="manual-reload-btn" style="padding:10px 20px;background:#2563eb;
        color:#fff;border:none;border-radius:6px;font-size:16px;">Recarregar</button>
    </div>
  `;
  document.getElementById('manual-reload-btn')?.addEventListener('click', () => {
    clearReloadCount();
    window.location.reload();
  });
};

class TenantLoader extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoaded: false,
      error: null
    };
  }

  async componentDidMount() {
    try {
      captureUtmParams();

      // Intercepta query string com token e redireciona para /sso-callback com hash
      // Isso deve acontecer antes de qualquer inicialização
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (token) {
        // Remove o token da query string e redireciona para /sso-callback com hash
        const currentPath = window.location.pathname;
        const newUrl = `${currentPath === '/' ? '' : currentPath}/sso-callback#token=${encodeURIComponent(token)}`;
        window.location.replace(newUrl);
        return; // Não continua a inicialização se houver redirecionamento
      }

      migrateToSessionStorage();
      
      // Inicializa o mapeamento de hosts dinamicamente antes de inicializar o tenant
      await initializeHostMapping();
      
      await initializeTenant();
      this.updateIconsForTenant();

      this.setState({ isLoaded: true });
      // Boot bem-sucedido: zera o histórico do circuit breaker de reload,
      // para que um erro futuro seja tratado como um incidente novo.
      clearReloadCount();
    } catch (error) {
      console.error('Failed to initialize app:', error);
      this.setState({ error: error.message, isLoaded: true });
    }
  }

  updateIconsForTenant() {
    const tenantId = getTenantId();
    
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
      const iconElements = document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]');
      iconElements.forEach(element => {
        const href = element.getAttribute('href');
        const newHref = href.replace('hosts/prospera/', `${process.env.REACT_APP_S3_BASE_URL}/${tenantId}/`);
        element.setAttribute('href', newHref);
      });
      
      const manifestElement = document.querySelector('link[rel="manifest"]');
      if (manifestElement) {
        manifestElement.setAttribute('href', `/manifest.json`);
      }
    }
  }

  render() {
    const { isLoaded, error } = this.state;

    if (!isLoaded) {
      return <Splash />;
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-screen">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Erro ao carregar o aplicativo</h1>
          <p className="text-gray-700">{error}</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => {
              // Descarta mapeamento/config de tenant em cache na aba para que o
              // reload busque tudo de novo (senão um cache ruim persiste no retry)
              try {
                ['hostTenantMapping', 'tenantId', 'tenantConfig'].forEach((key) => sessionStorage.removeItem(key));
              } catch {
                // ignora — storage pode estar bloqueado
              }
              window.location.reload();
            }}
          >
            Tentar novamente
          </button>
        </div>
      );
    }

    return (
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<TenantLoader />);

// Detecta falha ao carregar scripts/chunks (ex: deploy novo invalida hashes antigos)
// O servidor retorna HTML para o arquivo JS não encontrado → "Unexpected token '<'"
// Recarregar garante que index.html novo e os hashes corretos sejam buscados.
// Limitado a MAX_AUTO_RELOADS tentativas: como o reload é servido pelo precache
// do Service Worker (não pela rede), um chunk permanentemente quebrado causava
// loop infinito antes desse teto — depois dele, mostra uma tela manual.
window.addEventListener('error', (event) => {
  const { target } = event;
  if (target instanceof HTMLScriptElement || target instanceof HTMLLinkElement) {
    const count = getReloadCount();
    const willAutoReload = count < MAX_AUTO_RELOADS;

    logAssetLoadFailure({
      assetUrl: target.src || target.href,
      tagName: target.tagName,
      attempt: count + 1,
      maxAutoReloads: MAX_AUTO_RELOADS,
      outcome: willAutoReload ? 'auto-reload' : 'manual-fallback',
    });

    if (willAutoReload) {
      setReloadCount(count + 1);
      window.location.reload();
    } else {
      showManualReloadFallback();
    }
  }
}, true);

if ('serviceWorker' in navigator) {
  // Recarrega a página quando um novo SW assume o controle após deploy
  let isRefreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (isRefreshing) return;
    isRefreshing = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    // updateViaCache: 'none' ignora o Cache-Control do service-worker.js na checagem de update
    // (o navegador, por padrão, só é obrigado a rechecar ignorando cache 1x a cada 24h — sem isso,
    // um Cache-Control mal configurado no CDN/origem pode manter o SW antigo servindo por horas)
    navigator.serviceWorker.register('/service-worker.js', { scope: '/', updateViaCache: 'none' })
      .then((registration) => {
        // Recheca por uma versão nova sempre que o usuário volta pra aba, em vez de esperar
        // o navegador decidir sozinho (o que pode levar até 24h)
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            registration.update().catch(() => {});
          }
        });
      })
      .catch(error => {
        console.error('Falha no registro do Service Worker:', error);
      });
  });
}
